import { useState } from 'react';

import { Save, Edit2, Trash2 } from 'lucide-react';

import { PERMISSION_GROUPS } from '../utils/permissionGroups';
import { useDeleteRole } from '@/api/mutations/useDeleteRole';
import { useUpdateRole } from '@/api/mutations/useUpdateRole';
import { Role } from '@/api/queries/useGetRoles';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/new-button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';

interface RolePermissionsPanelProps {
  role: Role | null;
  editedName: string;
  editedPermissions: string[];
  onEditedNameChange: (name: string) => void;
  onEditedPermissionsChange: (permissions: string[]) => void;
  onRoleDeleted: () => void;
}

export const RolePermissionsPanel = ({
  role,
  editedName,
  editedPermissions,
  onEditedNameChange,
  onEditedPermissionsChange,
  onRoleDeleted,
}: RolePermissionsPanelProps) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate: updateRole, isLoading: isUpdating } = useUpdateRole();
  const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole();

  const hasUnsavedChanges =
    role &&
    (editedName !== role.name || JSON.stringify(editedPermissions.sort()) !== JSON.stringify(role.permissions.sort()));

  const handlePermissionChange = (permission: string, checked: boolean) => {
    if (checked) {
      onEditedPermissionsChange([...editedPermissions, permission]);
    } else {
      onEditedPermissionsChange(editedPermissions.filter((p) => p !== permission));
    }
  };

  const handleGroupPermissionChange = (groupPermissions: string[], checked: boolean) => {
    if (checked) {
      const newPermissions = [...editedPermissions];
      groupPermissions.forEach((permission) => {
        if (!newPermissions.includes(permission)) {
          newPermissions.push(permission);
        }
      });

      onEditedPermissionsChange(newPermissions);
    } else {
      onEditedPermissionsChange(editedPermissions.filter((p) => !groupPermissions.includes(p)));
    }
  };

  const handleSave = () => {
    if (!role || !editedName.trim() || editedName.length > 50) {
      return;
    }

    updateRole({
      roleId: role.id,
      name: editedName.trim(),
      permissions: editedPermissions,
    });
  };

  const handleDiscard = () => {
    if (role) {
      onEditedNameChange(role.name);
      onEditedPermissionsChange(role.permissions);
    }
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (role) {
      deleteRole(role.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          onRoleDeleted();
        },
      });
    }
  };

  if (!role) {
    return null;
  }

  const isAdministrator = role.name === 'Administrator';
  const canEdit = !isAdministrator;
  const canDelete = !isAdministrator && !role.isDefault && role.userCount === 0;

  const getDeleteTooltip = () => {
    if (role.isDefault) {
      return 'The default role cannot be deleted';
    }

    if (role.userCount > 0) {
      return `Roles can't be deleted while assigned to users`;
    }

    return '';
  };

  return (
    <div className="flex-1 p-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {isEditingName ? (
                    <div className="max-w-sm">
                      <Input
                        value={editedName}
                        onChange={(e) => {
                          onEditedNameChange(e.target.value);
                        }}
                        placeholder="Enter role name"
                        maxLength={50}
                        disabled={isUpdating}
                        className="text-2xl font-bold"
                        onBlur={() => setIsEditingName(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setIsEditingName(false);
                            handleSave();
                          }

                          if (e.key === 'Escape') {
                            onEditedNameChange(role.name);
                            setIsEditingName(false);
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-bold text-gray-900">{role.name}</h1>

                      {canEdit && (
                        <button
                          onClick={() => setIsEditingName(true)}
                          className="text-gray-400 transition-colors hover:text-gray-600"
                          disabled={isUpdating}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-sm text-gray-600">
                    {role.userCount} {role.userCount === 1 ? 'user' : 'users'} • {editedPermissions.length} permissions
                  </p>
                  {hasUnsavedChanges && <span className="text-sm font-medium text-orange-600">• Unsaved changes</span>}
                </div>
              </div>

              <div className="flex gap-2">
                {canEdit && (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSave}
                      disabled={isUpdating || !hasUnsavedChanges}
                    >
                      <Save className="h-4 w-4" />
                      Save
                    </Button>
                    {hasUnsavedChanges && (
                      <Button variant="outline" size="sm" onClick={handleDiscard} disabled={isUpdating}>
                        Discard
                      </Button>
                    )}
                  </>
                )}

                {!isAdministrator && (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <span className={!canDelete ? 'cursor-not-allowed' : ''}>
                          <Button variant="outline" size="sm" onClick={handleDeleteClick} disabled={!canDelete}>
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canDelete && (
                        <TooltipContent>
                          <p>{getDeleteTooltip()}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-start justify-between border-b border-gray-200 p-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Select permissions</h3>
                <p className="mt-1 text-xs text-gray-500">
                  Permissions define what actions users with this role can perform.
                </p>
              </div>

              {isAdministrator && (
                <div className="text-right">
                  <p className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-700">
                    Administrator role cannot be modified
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1 p-4">
              {PERMISSION_GROUPS.map((group) => {
                const groupPermissions = group.permissions.map((p) => p.permission);
                const selectedGroupPermissions = groupPermissions.filter((p) => editedPermissions.includes(p));
                const isGroupFullySelected = selectedGroupPermissions.length === groupPermissions.length;
                const isGroupPartiallySelected =
                  selectedGroupPermissions.length > 0 && selectedGroupPermissions.length < groupPermissions.length;

                return (
                  <div key={group.name} className="space-y-1">
                    {/* Group header */}
                    <div className="flex items-center rounded px-2 py-2 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isGroupFullySelected}
                          ref={(el) => {
                            if (el) el.indeterminate = isGroupPartiallySelected;
                          }}
                          onChange={(e) => handleGroupPermissionChange(groupPermissions, e.target.checked)}
                          disabled={isAdministrator || isUpdating || !canEdit}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-900">{group.name}</span>
                      </div>
                    </div>

                    {/* Individual permissions */}
                    {group.permissions.map((permissionInfo) => (
                      <div
                        key={permissionInfo.permission}
                        className="ml-6 flex items-center justify-between rounded px-2 py-2 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={editedPermissions.includes(permissionInfo.permission)}
                            onChange={(e) => handlePermissionChange(permissionInfo.permission, e.target.checked)}
                            disabled={isAdministrator || isUpdating || !canEdit}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="font-mono text-sm text-gray-600">{permissionInfo.permission}</span>
                        </div>
                        <div className="max-w-md text-right text-sm text-gray-500">{permissionInfo.description}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title="Delete Role"
        description={`Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        confirmButtonText="Delete Role"
        confirmButtonVariant="destructive"
      />
    </div>
  );
};
